import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'
import type { CurrentUserResponse } from '@/types/identity'

export type CurrentUserStatus =
  | 'loading'
  | 'authenticated'
  | 'profile_pending'
  | 'unauthorized'
  | 'not_configured'
  | 'error'

interface CurrentUserState {
  status: CurrentUserStatus
  data: CurrentUserResponse | null
  error: ApiError | Error | null
  retry: () => void
}

export function useCurrentUser(): CurrentUserState {
  const { gate, makeClient } = useApiClient()
  const [data, setData] = useState<CurrentUserResponse | null>(null)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [status, setStatus] = useState<CurrentUserStatus>('loading')
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    // AuthGate is still signing the user in — keep showing "loading",
    // matching the old no-account behavior.
    if (gate === 'not_signed_in') {
      setStatus('loading')
      return
    }
    if (gate === 'not_configured') {
      setStatus('not_configured')
      setData(null)
      setError(null)
      return
    }

    const controller = new AbortController()
    const client = makeClient()
    if (!client) {
      setStatus('loading')
      return
    }

    setStatus('loading')
    setError(null)

    client
      .get<CurrentUserResponse>('/api/me', { signal: controller.signal })
      .then((response) => {
        setData(response)
        setStatus(response.profileStatus === 'identity_only' ? 'profile_pending' : 'authenticated')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setData(null)
        setError(err)

        if (err instanceof ApiError && err.status === 401) {
          setStatus('unauthorized')
        } else {
          setStatus('error')
        }
      })

    return () => controller.abort()
  }, [gate, attempt, makeClient])

  return { status, data, error, retry }
}
